import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import dayjs from 'dayjs';
import {
  getAccountingEntries,
  getAccountingEntryClasses,
  getAccountingEntryTypes,
  getAccountingNodes,
  getAccountingPeriods,
  getCostCenters,
} from 'lib/api/accounting';
import GlobalSearcher from 'lib/common/GlobalSearcher';
import CustomAlert from 'lib/designSystem/Alert';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import PageHeader, { ActionButton } from 'lib/designSystem/PageHeader';
import {
  formatDateString,
  hasAnyPermissions,
  showAlert,
} from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AuthState } from '../../../../../../services/redux/authSlice';
import {
  AccountingEntry,
  AccountingEntryClass,
  AccountingNode,
  AccountingPeriod,
  CostCenter,
  EntryType,
} from '../../types';
import { toEndOfDayISO } from '../utils/fetchWithDateChunking';
import EditEntry from './components/EntryWithLines/EditEntry';
import BatchImporter from './components/Importer';
import AccountingEntriesTable from './components/Table';

const AccountingEntries: React.FC = () => {
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [data, setData] = useState<AccountingEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const [entryTypes, setEntryTypes] = useState<EntryType[]>([]);
  const [nodes, setNodes] = useState<AccountingNode[]>([]);
  const [entryClasses, setEntryClasses] = useState<AccountingEntryClass[]>([]);
  const [accountingPeriods, setAccountingPeriods] = useState<
    AccountingPeriod[]
  >([]);
  const defaultPeriod = React.useMemo(() => {
    const now = dayjs();
    return {
      start: now.startOf('month').format('YYYY-MM-DD'),
      end: now.endOf('month').format('YYYY-MM-DD'),
    };
  }, []);
  const [filterStartDate, setFilterStartDate] = useState<string | null>(
    defaultPeriod.start,
  );
  const [filterEndDate, setFilterEndDate] = useState<string | null>(
    defaultPeriod.end,
  );
  const [accountingEntryNumber, setAccountingEntryNumber] =
    useState<string>('');
  const [businessIdentifier, setBusinessIdentifier] = useState<string>('');
  const [businessName, setBusinessName] = useState<string>('');
  const [open, setOpen] = useState(false);
  const [openImporter, setOpenImporter] = useState(false);
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [entry, setEntry] = useState<AccountingEntry>({
    name: '',
    lines: [],
    date: new Date().toISOString().split('T')[0],
    typeId: 'free',
    classId: '',
    companyId: 0,
    status: 'CLOSED',
  });
  const [successText, setSuccessText] = useState<string | null>(null);
  const [failedText, setFailedText] = useState<string | null>(null);

  const buildQueryParams = (overrides?: {
    startDate?: string | null;
    endDate?: string | null;
  }) => {
    const params = new URLSearchParams();
    const s = overrides?.startDate ?? filterStartDate;
    const e = overrides?.endDate ?? filterEndDate;
    if (s != null) params.append('startDate', s);
    if (e != null) params.append('endDate', toEndOfDayISO(e));
    if (accountingEntryNumber)
      params.append('accountingEntryNumber', accountingEntryNumber);
    if (businessIdentifier)
      params.append('businessIdentifier', businessIdentifier);
    if (businessName) params.append('businessName', businessName);
    return params.toString();
  };

  const validateSomeFilter = (overrides?: {
    startDate?: string | null;
    endDate?: string | null;
  }) => {
    const s = overrides?.startDate ?? filterStartDate;
    const e = overrides?.endDate ?? filterEndDate;
    return (
      (s != null && s !== '') ||
      (e != null && e !== '') ||
      !!accountingEntryNumber ||
      !!businessIdentifier ||
      !!businessName
    );
  };
  const setUp = async (
    entryId?: number,
    overrides?: { startDate?: string | null; endDate?: string | null },
  ) => {
    const queryParams = buildQueryParams(overrides);
    if (!validateSomeFilter(overrides)) {
      return showAlert(
        dispatch,
        'warning',
        'Debe seleccionar al menos un filtro para buscar asientos.',
      );
    }
    try {
      setLoading(true);
      const response = await getAccountingEntries(dispatch, queryParams);
      setData(response);
      if (entryId) {
        const entrySetUp = response.find(
          (entry: AccountingEntry) => entry.id === entryId,
        );
        if (!entrySetUp) {
          setOpen(false);
          return setLoading(false);
        }

        const dateFormatted = entrySetUp.date.split('T')[0];
        setEntry({
          ...entrySetUp,
          date: dateFormatted,
        });
        setOpen(true);
      }
    } catch (error) {
      console.error('Error al cargar asientos:', error);
    }
    setLoading(false);
  };

  const setUpEntryEditing = async () => {
    try {
      const response = await getAccountingEntryTypes(dispatch);
      const responseNodes = await getAccountingNodes(dispatch);
      const responseEntryClasses = await getAccountingEntryClasses(dispatch);
      const responseCostCenters = await getCostCenters(dispatch);
      const responsePeriods = await getAccountingPeriods(dispatch);

      setEntryTypes(response);
      setNodes(responseNodes);
      setEntryClasses(responseEntryClasses);
      setCostCenters(responseCostCenters);
      setAccountingPeriods(responsePeriods);
    } catch (error) {
      console.error('Error al cargar datos para editar asientos:', error);
      setFailedText(
        'Error al cargar los datos necesarios para editar asientos.',
      );
    }
  };

  const handleNewEntry = () => {
    setOpen(true);
    setEntry({
      name: '',
      lines: [],
      date: new Date().toISOString().split('T')[0],
      typeId: 'free',
      classId: '',
      companyId: 0,
      status: 'CLOSED',
    });
  };

  const handleEditEntry = (entryId: number) => {
    const entry = data.find((entry: AccountingEntry) => entry.id === entryId);
    if (!entry) return alert('No se encontró la entrada.');

    const dateFormatted = entry.date.split('T')[0];
    setEntry({
      ...entry,
      date: dateFormatted,
    });
    setOpen(true);
  };

  useEffect(() => {
    void setUp();
    void setUpEntryEditing();
  }, []);

  const periodSubtitle =
    filterStartDate != null && filterEndDate != null
      ? `Período: ${formatDateString(filterStartDate, 'dd/mm/yyyy')} - ${formatDateString(filterEndDate, 'dd/mm/yyyy')}`
      : undefined;

  const filters: React.ComponentProps<typeof GlobalSearcher>['filters'] = [
    {
      type: 'monthly',
      label: 'Período mensual',
      dateKey: 'startDate',
      value: filterStartDate ?? '',
      setter: (val) => setFilterStartDate(val || null),
    },
    {
      type: 'monthly',
      label: 'Período mensual',
      dateKey: 'endDate',
      value: filterEndDate ?? '',
      setter: (val) => setFilterEndDate(val || null),
    },
    {
      type: 'text',
      label: 'ID comprobante',
      value: accountingEntryNumber,
      setter: setAccountingEntryNumber,
    },
    {
      type: 'text',
      label: 'RUT cliente',
      value: businessIdentifier,
      setter: setBusinessIdentifier,
    },
    {
      type: 'text',
      label: 'Nombre cliente',
      value: businessName,
      setter: setBusinessName,
    },
  ];

  const actionButtons: ActionButton[] = [
    {
      label: 'Crear',
      color: 'primary',
      onClick: () => handleNewEntry(),
      disabled: !hasAnyPermissions(user, ['accounting_write_entries']),
    },
    {
      label: 'Importar',
      color: 'primary',
      onClick: () => setOpenImporter(true),
      disabled: !hasAnyPermissions(user, ['accounting_write_entries']),
    },
  ];

  if (loading) return <CenteredCircularProgress type="layout" />;
  return (
    <div>
      <PageHeader.WithTitleAndActions
        title="Asientos contables"
        subtitle={periodSubtitle}
        iconType="calendar"
        actionButtons={actionButtons}
        globalSearcherProps={{
          filters,
          triggerSearch: async () => {
            await setUp(undefined, {
              startDate: filterStartDate,
              endDate: filterEndDate,
            });
          },
          shortcuts: true,
        }}
      />
      <div>
        <AccountingEntriesTable
          entries={data}
          handleEditEntry={handleEditEntry}
          nodes={nodes}
          costCenters={costCenters}
        />
      </div>
      {open && (
        <EditEntry
          entry={entry}
          setEntry={setEntry}
          open={open}
          setOpen={setOpen}
          setUp={setUp}
          setSuccessText={setSuccessText}
          setFailedText={setFailedText}
          entryTypes={entryTypes}
          nodes={nodes}
          entryClasses={entryClasses}
          accountingPeriods={accountingPeriods}
        />
      )}
      <CustomAlert
        open={!!successText}
        onClose={() => setSuccessText(null)}
        severity="success"
        text={successText}
      />
      <CustomAlert
        open={!!failedText}
        onClose={() => setFailedText(null)}
        severity="error"
        text={failedText}
      />
      <BatchImporter
        open={openImporter}
        setOpen={setOpenImporter}
        nodes={nodes}
        entryClasses={entryClasses}
        accountingPeriods={accountingPeriods}
        companyId={user?.companyId || 0}
        onSuccess={() => void setUp()}
      />
    </div>
  );
};

export default AccountingEntries;
